import type { BookingResponse, OtaReservationStatus } from '@/shared/api';

export interface BookingGroupEntry {
  kind: 'group';
  reservationId: string;
  reservationCode: string | null;
  channel: string | null;
  rows: BookingResponse[];
  /**
   * Storno-Zustand der Reservierung, serverseitig aus den **sichtbaren** Kindern gebildet.
   * `PartiallyCancelled` kann es deshalb nur in einer Gruppe geben — bei einem einzigen sichtbaren
   * Kind sagt schon dessen Buchungsstatus dasselbe.
   */
  reservationStatus: OtaReservationStatus | null;
  /** Wie viele Kinder dieser Nutzer sehen darf — nie die echte Groesse der Reservierung. */
  visibleSiblingCount: number;
  /** Auf dieser Seite liegen weniger Kinder als sichtbar existieren. */
  isPartial: boolean;
  /** Summe ausschliesslich der hier gelieferten Zeilen. Nie hochgerechnet. */
  visibleTotal: number | null;
  currency: string;
  checkInDate: string;
  checkOutDate: string;
}

export interface BookingSingleEntry {
  kind: 'single';
  row: BookingResponse;
}

export type BookingListEntry = BookingGroupEntry | BookingSingleEntry;

const amountOf = (row: BookingResponse) =>
  row.totalPrice === null ||
  (row.source === 'Ota' && (row.priceEvidenceState ?? 'Known') !== 'Known')
    ? null
    : (row.grandTotal ?? row.totalPrice);

/**
 * Fasst die Kinder derselben OTA-Reservierung zu einer Gruppe zusammen. Gruppiert wird nach
 * Reservierungs-Id und nicht nach Nachbarschaft: die Gruppe verankert sich an der Position ihres
 * ersten Kindes, damit ein Client-Sort sie nicht zerreissen kann.
 *
 * Eine Reservierung mit genau einem sichtbaren Kind ist keine Gruppe — sonst wuerde eine
 * Gruppenzeile die Existenz nicht zugaenglicher Apartments andeuten.
 */
export function groupBookings(rows: BookingResponse[]): BookingListEntry[] {
  const entries: BookingListEntry[] = [];
  const groupIndexById = new Map<string, number>();

  for (const row of rows) {
    const reservationId = row.otaReservationId;
    if (!reservationId || (row.visibleSiblingCount ?? 0) <= 1) {
      entries.push({ kind: 'single', row });
      continue;
    }

    const existing = groupIndexById.get(reservationId);
    if (existing === undefined) {
      groupIndexById.set(reservationId, entries.length);
      entries.push({
        kind: 'group',
        reservationId,
        reservationCode: row.otaReservationCode ?? null,
        channel: row.channel ?? null,
        rows: [row],
        reservationStatus: row.reservationStatus ?? null,
        visibleSiblingCount: row.visibleSiblingCount ?? 0,
        isPartial: row.groupIsPartial ?? false,
        visibleTotal: amountOf(row),
        currency: row.currency,
        checkInDate: row.checkInDate,
        checkOutDate: row.checkOutDate,
      });
      continue;
    }

    const group = entries[existing] as BookingGroupEntry;
    group.rows.push(row);
    const amount = amountOf(row);
    group.visibleTotal =
      group.visibleTotal === null || amount === null ? null : group.visibleTotal + amount;
    if (row.checkInDate < group.checkInDate) group.checkInDate = row.checkInDate;
    if (row.checkOutDate > group.checkOutDate) group.checkOutDate = row.checkOutDate;
  }

  return entries;
}
